/*
 * Discount code entry inside the cart.
 *
 * /cart/update.js takes a `discount` field: a code applies it, an empty string
 * clears it, and it hands back freshly rendered sections in the same call.
 *
 * Two things it does NOT do: it returns 200 whether or not the code was real,
 * and a bad code silently clears whatever was already applied. So we read the
 * cart back to find out what actually happened, and if a typo wiped a working
 * code we put the old one back rather than leaving the shopper worse off.
 *
 * Custom element, because the cart drawer re-renders with innerHTML.
 */

const CDC_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

class CartDiscount extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-discount-input]');
    this.button = this.querySelector('[data-discount-apply]');
    this.status = this.querySelector('[data-discount-status]');

    if (this.button) {
      this.button.addEventListener('click', (e) => {
        e.preventDefault();
        this.submit();
      });
    }

    const toggle = this.querySelector('.cdc__toggle');
    if (toggle && this.input) {
      toggle.addEventListener('toggle', () => {
        if (toggle.open) this.input.focus();
      });
    }

    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        this.submit();
      });
    }

    this.querySelectorAll('[data-discount-remove]').forEach((el) => {
      el.addEventListener('click', (e) => {
        e.preventDefault();
        this.submit('');
      });
    });
  }

  setStatus(message, kind) {
    if (!this.status) return;
    // A message lives inside the folded code box, so unfold it to show the message.
    const toggle = this.querySelector('.cdc__toggle');
    if (toggle && message) toggle.open = true;
    this.status.textContent = message || '';
    this.status.className = 'cdc__status' + (kind ? ' cdc__status--' + kind : '');
  }

  setBusy(busy) {
    if (this.button) this.button.disabled = busy;
    if (this.input) this.input.disabled = busy;
  }

  async appliedCode() {
    try {
      const cart = await (await fetch('/cart.js', { credentials: 'same-origin' })).json();
      const applied = cart.cart_level_discount_applications || [];
      return applied.length ? applied[0].title : null;
    } catch (e) {
      return null;
    }
  }

  /* Sends the code and returns the rendered sections, or null if the call failed. */
  async send(code) {
    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          discount: code,
          sections: CDC_SECTIONS,
          sections_url: window.location.pathname,
        }),
      });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async submit(explicitCode) {
    const code = explicitCode !== undefined ? explicitCode : this.input ? this.input.value.trim() : '';
    const removing = code === '';

    if (!removing && !code) {
      this.setStatus('Enter a code first.', 'error');
      return;
    }

    this.setBusy(true);
    this.setStatus(removing ? 'Removing…' : 'Applying…');

    const previous = removing ? null : await this.appliedCode();

    let state = await this.send(code);
    if (!state) {
      this.setBusy(false);
      this.setStatus('Could not reach the server. Try again.', 'error');
      return;
    }

    if (!removing) {
      const now = await this.appliedCode();
      const worked = now && now.toUpperCase() === code.toUpperCase();

      if (!worked) {
        if (previous) {
          // The bad code wiped a working one. Put it back.
          state = (await this.send(previous)) || state;
          this.setBusy(false);
          this.render(state, 'That code isn’t valid — ' + previous + ' is still applied.', 'error');
          return;
        }
        this.setBusy(false);
        this.render(state, 'That code isn’t valid for this cart.', 'error');
        return;
      }
    }

    this.render(state, '', null);
  }

  /*
   * Re-renders the cart, then puts the message back — the element that holds it
   * is replaced by the render, so the message has to be re-applied afterwards.
   */
  render(state, message, kind) {
    const drawer = document.querySelector('cart-drawer');

    if (!drawer || !state || !state.sections) {
      if (message) {
        this.setBusy(false);
        this.setStatus(message, kind);
        return;
      }
      window.location.reload();
      return;
    }

    drawer.renderContents(state);

    if (!message) return;
    setTimeout(() => {
      const fresh = document.querySelector('#CartDrawer cart-discount');
      if (fresh) fresh.setStatus(message, kind);
    }, 60);
  }
}

if (!customElements.get('cart-discount')) {
  customElements.define('cart-discount', CartDiscount);
}
